import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, ViewChild, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { TagModel } from '@core/models/tag.model';
import * as fromRoot from '@core/redux';
import * as TagActions from '@core/redux/tag/tag.actions';
import { Store } from '@ngrx/store';
import { TagComponent } from '@ux/components/tag/tag.component';

interface ColorOption {
  color: string;
  cssClassApi: string;
  cssClass: string;
}

@Component({
  selector: 'app-setting-tag-item',
  templateUrl: './setting-tag-item.component.html',
  styleUrls: ['./setting-tag-item.component.less'],
  standalone: true,
  imports: [TagComponent, FormsModule, CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingTagItemComponent {
  @Input({ required: true }) set tag(value: TagModel) {
    if (this._originalTag() !== value) {
      this._originalTag.set(value);
      if (this._isNew()) {
        this.clearForm();
      } else {
        this.copyModel();
      }
    }
  }
  get tag(): TagModel {
    return this._originalTag();
  }

  @Input() set isNew(value: boolean) {
    this._isNew.set(value);
  }
  get isNew(): boolean {
    return this._isNew();
  }

  @ViewChild(TagComponent) tagComponent!: TagComponent;

  readonly cssClasses: ColorOption[] = [
    {
      color: '#42e3f0',
      cssClassApi: 'tag_bg-1',
      cssClass: 'setting-tag-item__color_bg-1',
    },
    {
      color: '#5fb0ff',
      cssClassApi: 'tag_bg-3',
      cssClass: 'setting-tag-item__color_bg-3',
    },
    {
      color: '#995ec9',
      cssClassApi: 'tag_bg-4',
      cssClass: 'setting-tag-item__color_bg-4',
    },
    {
      color: '#ff7ea4',
      cssClassApi: 'tag_bg-5',
      cssClass: 'setting-tag-item__color_bg-5',
    },
    {
      color: '#ffdea2',
      cssClassApi: 'tag_bg-6',
      cssClass: 'setting-tag-item__color_bg-6',
    },
    {
      color: '#a4e279',
      cssClassApi: 'tag_bg-7',
      cssClass: 'setting-tag-item__color_bg-7',
    },
    {
      color: '#dfe3a3',
      cssClassApi: 'tag_bg-8',
      cssClass: 'setting-tag-item__color_bg-8',
    },
  ];

  private readonly _isNew = signal(false);
  private readonly _originalTag = signal<TagModel>(new TagModel());
  private readonly store = inject<Store<fromRoot.State>>(Store);

  protected readonly isEditMode = signal(false);
  protected readonly tagCopy = signal<TagModel>(new TagModel());

  constructor() {
    if (this.isNew) {
      this.clearForm();
    }
  }

  onSave(): void {
    const currentTag = this.tagCopy();
    if (currentTag.cssClass && currentTag.name) {
      if (this._isNew()) {
        this.store.dispatch(new TagActions.CreateAction(currentTag));
        this.clearForm();
      } else {
        this.store.dispatch(new TagActions.SaveAction(currentTag));
      }
    }
  }

  onFocus(): void {
    this.isEditMode.set(true);
  }

  onBlur(): void {
    if (this.tagCopy().name !== this._originalTag().name) {
      this.onSave();
    }
    this.isEditMode.set(false);
  }

  onDelete(): void {
    this.store.dispatch(new TagActions.DeleteAction(this._originalTag()));
  }

  onChangeCssClass(cssClass: string): void {
    const currentTag = this.tagCopy();
    const updatedTag = new TagModel();
    updatedTag.setAttributes({ ...currentTag, cssClass });
    this.tagCopy.set(updatedTag);
    this.tagComponent.updateCssClass(cssClass);
    this.onSave();
  }

  private copyModel(): void {
    const newTag = new TagModel();
    newTag.setAttributes(this._originalTag());
    this.tagCopy.set(newTag);
  }

  private getRandomCssClass(): string {
    const randomIndex = Math.floor(Math.random() * this.cssClasses.length);
    return this.cssClasses[randomIndex].cssClassApi;
  }

  private clearForm(): void {
    const newTag = new TagModel();
    newTag.setAttributes({ cssClass: this.getRandomCssClass() });
    this.tagCopy.set(newTag);
  }
}
